import {useEffect, useState, useRef} from 'react'
import {OnDashPageTitle} from '../cores/OnDashPageData'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import {getDownloadURL, ref} from 'firebase/storage'
import db, {storage} from '../../../services/firebase'
import {useAuth} from '../../modules/auth'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import { doc, getDoc,serverTimestamp, setDoc} from 'firebase/firestore'
import {format} from 'date-fns'
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast'
import { ToastContainer } from 'react-toastify'

let WebViewer: any = null
if (typeof window !== 'undefined') {
  import('@pdftron/pdfjs-express-viewer').then((module) => {
    WebViewer = module.default
  })
}

interface WebViewerInstance {
  dispose: () => void
  Core: any
  UI: any
}

interface WebViewerOptions {
  path: string
  initialDoc: string
  enableAnnotations?: boolean
  licenseKey?: string
  disabledElements?: string[]
  customCss?: string
  initialZoom?: number
  initialViewMode?: string
  pageTransition?: string
}

const TrainingViewer = () => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)
  const [completeDate, setCompleteDate] = useState<any>(null)
  const [lastPage, setLastPage] = useState(false)
  const [isCompleting, setIsCompleting] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [isInitialLoading, setIsInitialLoading] = useState(true)
  const {currentUser} = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const viewerRef = useRef<HTMLDivElement>(null)
  const webViewerInstance = useRef<WebViewerInstance | null>(null)

  const trainingType = location.pathname.split('/').pop()?.toUpperCase() || 'SAT'

  const trainingConfig = {
    SAT: {
      title: 'Security Awareness Training',
      desc: 'Safeguard your digital workspace.',
      icon: 'secu.svg',
      pdfPath: '/SAT_training_pdf/Security Awareness Training.pdf'
    },
    GDPR: {
      title: 'GDPR Training',
      desc: 'Protect data privacy under GDPR regulations.',
      icon: 'GDPRT.svg', 
      pdfPath: '/GDPR_training_pdf/GDPR Training.pdf'
    }
  }

  const config = trainingConfig[trainingType] || trainingConfig.SAT

  useEffect(() => {
    const fetchPdfUrl = async () => {
      if (!currentUser) {
        setError('User is not authenticated')
        setIsInitialLoading(false)
        return
      }
      try {
        setIsInitialLoading(true)
        const pdfRef = ref(storage, config.pdfPath)
        const url = await getDownloadURL(pdfRef)
        setPdfUrl(url)
        
        const response = await fetch(url)
        const reader = response.body?.getReader()
        const contentLength = +(response.headers.get('Content-Length') ?? '0')

        let receivedLength = 0
        const chunks: Uint8Array[] = []

        while (true) {
          if (!reader) throw new Error('Failed to initialize PDF reader')
          const {done, value} = await reader.read()

          if (done) break

          chunks.push(value)
          receivedLength += value.length

          setLoadingProgress((receivedLength / contentLength) * 100)
        }

        const blob = new Blob(chunks)
        const blobUrl = URL.createObjectURL(blob)

        setPdfUrl(blobUrl)
        localStorage.setItem(`${trainingType.toLowerCase()}TrainingPdfUrl`, blobUrl)
      } catch (error) {
        console.error('Error fetching PDF URL:', error)
        setError('Failed to load PDF')
      } finally {
        setIsInitialLoading(false)
      }
    }
    fetchPdfUrl()
  }, [currentUser, config.pdfPath])

  useEffect(() => {
    const fetchTrainingStatus = async () => {
      if (!currentUser?.tenantId || !currentUser?.uid) return

      try {
        const trainingDoc = await getDoc(
          doc(db, 'tenants', currentUser.tenantId, 'onboarding', currentUser.uid, 'training', trainingType)
        )
        const data = trainingDoc.data()
        setCompleted(data?.is_checked || false)
        setCompleteDate(data?.completed_at?.toDate())
      } catch (error) {
        console.error('Error fetching training status:', error)
      }
    }
    fetchTrainingStatus()
  }, [currentUser, trainingType])

  useEffect(() => {
    const cleanupWebViewer = () => {
      if (webViewerInstance.current) {
        try {
          webViewerInstance.current.dispose()
        } catch (err) {
          console.error('Error disposing WebViewer:', err)
        }
        webViewerInstance.current = null
      }
    }

    cleanupWebViewer()

    if (pdfUrl && viewerRef.current) {
      const initWebViewer = async () => {
        try {
          setError(null)

          if (!WebViewer) {
            await new Promise((resolve) => {
              const checkWebViewer = () => {
                if (WebViewer) {
                  resolve(true)
                } else {
                  setTimeout(checkWebViewer, 100)
                }
              }
              checkWebViewer()
            })
          }

          if (viewerRef.current) {
            viewerRef.current.innerHTML = ''
          }

          const options: WebViewerOptions = {
            path: '/webviewer/lib',
            initialDoc: pdfUrl,
            enableAnnotations: false,
            licenseKey: process.env.REACT_APP_PDFJS_EXPRESS_VIEWER_LICENSE,
            disabledElements: ['header', 'toolsHeader', 'ribbons'],
            initialViewMode: 'Facing',
            pageTransition: 'PageCurl',
          }

          const instance = (await WebViewer(options, viewerRef.current)) as WebViewerInstance

          webViewerInstance.current = instance

          if (instance.Core && instance.Core.documentViewer) {
            instance.Core.documentViewer.addEventListener('pageNumberUpdated', (pageNumber: number) => {
              const totalPages = instance.Core.documentViewer.getPageCount()
              setLastPage(pageNumber === totalPages)
            })
          }

        } catch (err) {
          console.error('Error initializing WebViewer:', err)
          setError('Failed to load PDF document')
        }
      }

      const timer = setTimeout(() => {
        initWebViewer()
      }, 200)

      return () => {
        clearTimeout(timer)
        cleanupWebViewer()
      }
    }

    return () => {
      cleanupWebViewer()
    }
  }, [pdfUrl])

  const handleCheckboxChange = async () => {
    if (!currentUser?.tenantId || !currentUser?.uid || isCompleting) return

    setIsCompleting(true)
    try {
      await setDoc(
        doc(db, 'tenants', currentUser.tenantId, 'onboarding', currentUser.uid, 'training', trainingType),
        {
          download_url: pdfUrl,
          is_checked: true,
          name: config.title,
          completed_at: serverTimestamp(),
        },
        {merge: true}
      )
      showSuccessToast({message: 'Training completed successfully'})
      setCompleted(true)
      setTimeout(() => navigate('/training'), 2000)
    } catch (error) {
      showErrorToast({message: 'Failed to complete training'})
    } finally {
      setIsCompleting(false)
    }
  }

  return (
    <>
      <div className='d-flex align-items-center mb-2 mt-0'>
        <Link to='/training'>
          <img src={toAbsoluteUrl('/media/logos/leftButton.svg')} alt='back' className='p-2' />
        </Link>
        <Link to='/training' className='text-decoration-none text-inherit'>
          <label className='cursor-pointer text-gray-700 ms-1'>Back To Training</label>
        </Link>
      </div>

      <OnDashPageTitle
        icon={config.icon}
        title={config.title}
        desc={config.desc}
      />

      <div className='card'>
        <div className='row'>
          <div className='col-md-12'>
            <div className='row m-0'>
              <div className='col-md-3 border-end h-100' style={{minHeight: '500px'}}>
                <h2 className='text-dark mb-1 mt-3 ml-4 p-4'>Contents</h2>
                <hr />
                <div className='d-flex align-items-center p-3 bg-gray-100 gap-2' style={{borderRadius:'8px'}}>
                  {completed ? 
                    <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="20"  viewBox="0 0 24 24"  fill="#0e9f6e"  className="icon icon-tabler icons-tabler-filled icon-tabler-circle-check"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" /></svg>
                  :
                  <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="#6a7282"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-circle-dashed"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8.56 3.69a9 9 0 0 0 -2.92 1.95" /><path d="M3.69 8.56a9 9 0 0 0 -.69 3.44" /><path d="M3.69 15.44a9 9 0 0 0 1.95 2.92" /><path d="M8.56 20.31a9 9 0 0 0 3.44 .69" /><path d="M15.44 20.31a9 9 0 0 0 2.92 -1.95" /><path d="M20.31 15.44a9 9 0 0 0 .69 -3.44" /><path d="M20.31 8.56a9 9 0 0 0 -1.95 -2.92" /><path d="M15.44 3.69a9 9 0 0 0 -3.44 -.69" /></svg>
                  }
                    <div className='text-dark fw-bold text-hover-primary fs-6'>
                      {config.title}
                    </div>
                </div>
              </div>

              <div className='col-md-9 px-0'>
                <div className='mb-0'>
                  <div className='overlay'>
                    {error ? (
                      <div className='container-fluid'>
                        <div className='row'>
                          <div
                            className='col d-flex justify-content-center align-items-center'
                            style={{height: 600}}
                          >
                            <div className='text-danger'>{error}</div>
                          </div>
                        </div>
                      </div>
                    ) : isInitialLoading || !pdfUrl ? (
                      <div className='container-fluid'>
                        <div className='row'>
                          <div
                            className='col d-flex justify-content-center align-items-center'
                            style={{height: 600}}
                          >
                            <div className='text-center'>
                              <div className='spinner-border text-primary mb-3' role='status'>
                                <span className='visually-hidden'>Loading...</span>
                              </div>
                              <div className='text-gray-700'>Loading training document...</div>
                              {loadingProgress > 0 && (
                                <div className='mt-2'>
                                  <div className='progress' style={{width: '200px', height: '4px'}}>
                                    <div 
                                      className='progress-bar' 
                                      style={{width: `${loadingProgress}%`}}
                                    ></div>
                                  </div>
                                  <small className='text-muted'>{Math.round(loadingProgress)}%</small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className='container-fluid'>
                        <div className='row'>
                          <div className='col p-0' style={{height: 600}}>
                            <div
                              ref={viewerRef}
                              style={{
                                height: '100%',
                                width: '100%',
                                minHeight: '400px',
                                backgroundColor: '#f5f5f5',
                              }}
                              id='pdf-viewer-container'
                            />

                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className='border p-4 ml-3'>
                  {completed ? (
                    <button
                      type='button'
                      className='btn me-3 btn-sm fs-6'
                      disabled
                      style={{backgroundColor: '#0E9F6E', color: '#fff', height: '40px'}}
                    >
                      Completed
                    </button>
                  ) : lastPage ? (
                    <button
                      type='button'
                      className='btn me-3 btn-sm fs-6'
                      style={{
                        backgroundColor: '#0E9F6E',
                        color: '#fff',
                        height: '40px',
                        minWidth: '150px',
                      }}
                      onClick={handleCheckboxChange}
                      disabled={isCompleting}
                    >
                      {isCompleting ? (
                        <>
                          Please wait...
                          <span className='spinner-border spinner-border-sm me-2' role='status' />
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='18'
                            height='18'
                            viewBox='0 0 24 24'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            className='icon icon-tabler icons-tabler-outline icon-tabler-check mx-1 mb-1'
                          >
                            <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                            <path d='M5 12l5 5l10 -10' />
                          </svg>
                          Mark as Complete
                        </>
                      )}
                    </button>
                  ) : (
                    <button
                      type='button'
                      className='btn me-3 btn-sm fs-6'
                      style={{backgroundColor: '#A2E3D0', color: '#fff', height: '40px'}}
                      disabled
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='18'
                        height='18'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-check me-2 '
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M5 12l5 5l10 -10' />
                      </svg>
                      Mark as Complete
                    </button>
                  )}
                  {completed ? (
                    <span className='p-1 fs-6 text-gray-700'>
                      Completed on:{' '}
                      {completeDate ? format(completeDate, "MMMM dd, yyyy 'at' h:mm:ss a") : ''}
                    </span>
                  ) : (
                    <span className='p-1 fs-6 text-gray-900'>
                      Please note that you are required to read all the training material in order
                      to complete the training.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  )
}

export default TrainingViewer